/**
 * GameConfig — the tunable balance values injected into a match at {@link createInitialState}.
 *
 * Defaults are today's compiled constants, so a match with NO overrides is byte-identical (the
 * §5/§5b gates are unaffected). The point of the seam: the authoritative match host (the server) can
 * override these PER-MATCH from remote config (Cloudflare KV) → live balance tuning with no deploy
 * and no app-store review. The deterministic harness validates a config before it ships.
 *
 * v1 covers the init-time economy/timing knobs that flow cleanly through existing state fields.
 * Runtime knobs (scoring zones, meter windows, physics) join as the seam grows — those will thread a
 * resolved config onto the GameState so the reducer can read them.
 */
import { DEFENSE_CHARGE, ENERGY_MAX, ENERGY_START, MATCH_DURATION_SECONDS } from './constants.ts';

export interface GameConfig {
  /** Match length in seconds (the countdown clock). */
  readonly matchDuration: number;
  /** Energy each side starts with. */
  readonly energyStart: number;
  /** Energy cap (the regen ceiling). */
  readonly energyMax: number;
  /** Defense charges each side starts with (anti-turtle economy). */
  readonly defenseChargeStart: number;
}

/** The compiled defaults — a match built from these is identical to before the seam existed. */
export const DEFAULT_CONFIG: GameConfig = {
  matchDuration: MATCH_DURATION_SECONDS,
  energyStart: ENERGY_START,
  energyMax: ENERGY_MAX,
  defenseChargeStart: DEFENSE_CHARGE.start,
};

/** A finite, in-range number, or the fallback — guards against a bad remote-config value. */
function num(v: unknown, fallback: number, min: number, max: number): number {
  return typeof v === 'number' && Number.isFinite(v) ? Math.max(min, Math.min(max, v)) : fallback;
}

/** Merge overrides onto the defaults, sanitising each value so a bad remote config can't break play. */
export function resolveConfig(overrides?: Partial<GameConfig>): GameConfig {
  if (!overrides) return DEFAULT_CONFIG;
  return {
    matchDuration: num(overrides.matchDuration, DEFAULT_CONFIG.matchDuration, 30, 1200),
    energyStart: num(overrides.energyStart, DEFAULT_CONFIG.energyStart, 0, 100),
    energyMax: num(overrides.energyMax, DEFAULT_CONFIG.energyMax, 1, 100),
    defenseChargeStart: num(overrides.defenseChargeStart, DEFAULT_CONFIG.defenseChargeStart, 0, 20),
  };
}
